import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { formatCents, formatQty, type Paginated, type StockBalance } from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage, fieldErrors } from '@/lib/errors';
import { T } from '@/design/tokens';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { Field, FormSection } from '@/components/FormSection';
import { MoneyInput, QtyInput } from '@/components/MoneyInput';
import { useCostCenters, useItems, useStockLocations } from '@/lib/queries';
import { DateInput } from '@/components/DateInput';

/**
 * As quatro ações que mexem no estoque.
 *
 * Cada uma é um Modal (ação, não consulta) e nenhuma delas edita o passado:
 * entrada, saída, transferência e ajuste sempre GERAM uma linha nova no
 * histórico. O ajuste é o único que exige justificativa, porque é o único que
 * muda saldo sem um documento por trás.
 */

/** Saldo atual da dupla item+local, para o operador não sair no escuro. */
function useBalance(itemId: string, locationId: string) {
  return useQuery({
    queryKey: ['stock-balances', 'one', itemId, locationId],
    queryFn: () =>
      api.get<Paginated<StockBalance>>('/stock-balances', {
        itemId,
        locationId,
        hideZero: 'false',
        perPage: 1,
      }),
    enabled: Boolean(itemId && locationId),
    select: (response) => response.data[0] ?? null,
  });
}

function useResetOnOpen(open: boolean, reset: () => void) {
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) reset();
  }
}

function ErrorBanner({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
      {errorMessage(error)}
    </div>
  );
}

function BalanceHint({ itemId, locationId }: { itemId: string; locationId: string }) {
  const balance = useBalance(itemId, locationId);
  if (!itemId || !locationId) return null;
  const row = balance.data;
  return (
    <p className="muted" style={{ fontSize: 12.5, marginTop: -4, marginBottom: 12 }}>
      {row
        ? `Saldo atual neste local: ${formatQty(row.quantityMilli)} ${row.unit} · custo médio ${formatCents(row.avgCostCents)}`
        : 'Sem saldo registrado neste local ainda.'}
    </p>
  );
}

/* ================================ Entrada ================================ */

export function EntryDialog({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const items = useItems();
  const locations = useStockLocations();

  const [itemId, setItemId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [quantityMilli, setQuantityMilli] = useState<number | null>(null);
  const [unitCostCents, setUnitCostCents] = useState<number | null>(null);
  const [batchCode, setBatchCode] = useState('');
  const [batchExpiry, setBatchExpiry] = useState('');
  const [reason, setReason] = useState('');

  useResetOnOpen(open, () => {
    setItemId('');
    setLocationId('');
    setQuantityMilli(null);
    setUnitCostCents(null);
    setBatchCode('');
    setBatchExpiry('');
    setReason('');
  });

  // Só o item marcado no cadastro pede lote. Mostrar o campo para todo mundo
  // faria o almoxarife inventar um número para se livrar dele.
  const exigeLote = (items.data ?? []).find((item) => item.id === itemId)?.tracksBatch ?? false;

  const mutation = useMutation({
    mutationFn: (body: unknown) => api.post('/stock-movements/entry', body),
    onSuccess: () => {
      onDone();
      onClose();
    },
  });

  const errors = fieldErrors(mutation.error);
  const total = (quantityMilli ?? 0) * (unitCostCents ?? 0);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Entrada de material"
      description="Entrada avulsa, sem pedido de compra — o custo é obrigatório"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            loading={mutation.isPending}
            disabled={
              !itemId ||
              !locationId ||
              !quantityMilli ||
              unitCostCents === null ||
              (exigeLote && !batchCode.trim())
            }
            onClick={() =>
              mutation.mutate({
                itemId,
                locationId,
                quantityMilli,
                unitCostCents,
                batch: exigeLote
                  ? { code: batchCode.trim(), expiryDate: batchExpiry || null }
                  : null,
                reason: reason.trim() || null,
              })
            }
          >
            Dar entrada
          </Button>
        </>
      }
    >
      <ErrorBanner error={mutation.error} />
      <FormSection>
        <Field label="Item" htmlFor="entry-item" error={errors.itemId}>
          <select
            id="entry-item"
            className="input"
            value={itemId}
            onChange={(event) => setItemId(event.target.value)}
          >
            <option value="">Selecione…</option>
            {(items.data ?? []).map((item) => (
              <option key={item.id} value={item.id}>
                {item.sku} · {item.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Local de destino" htmlFor="entry-location" error={errors.locationId}>
          <select
            id="entry-location"
            className="input"
            value={locationId}
            onChange={(event) => setLocationId(event.target.value)}
          >
            <option value="">Selecione…</option>
            {(locations.data ?? []).map((location) => (
              <option key={location.id} value={location.id}>
                {location.code} · {location.name}
              </option>
            ))}
          </select>
        </Field>

        <BalanceHint itemId={itemId} locationId={locationId} />

        <div className="grid-2">
          <Field label="Quantidade" htmlFor="entry-qty" error={errors.quantityMilli}>
            <QtyInput id="entry-qty" value={quantityMilli} onChange={setQuantityMilli} />
          </Field>
          <Field
            label="Custo unitário"
            htmlFor="entry-cost"
            error={errors.unitCostCents}
            hint="Entra na média ponderada do item"
          >
            <MoneyInput id="entry-cost" value={unitCostCents} onChange={setUnitCostCents} />
          </Field>
        </div>

        {total > 0 && (
          <p className="mono" style={{ fontSize: 13, color: T.inkSoft, marginTop: -4 }}>
            Total da entrada: <strong>{formatCents(Math.round(total / 1000))}</strong>
          </p>
        )}

        {exigeLote && (
          <div className="grid-2">
            <Field
              label="Lote"
              htmlFor="entry-batch"
              error={errors.batch}
              hint="Como está escrito na embalagem"
            >
              <input
                id="entry-batch"
                className="input input--mono"
                value={batchCode}
                onChange={(event) => setBatchCode(event.target.value)}
                placeholder="L2026A"
              />
            </Field>
            <Field
              label="Validade"
              htmlFor="entry-expiry"
              hint="Em branco só se o lote realmente não vencer"
            >
              <DateInput
                id="entry-expiry"
                className="input input--mono"
                value={batchExpiry}
                onChange={setBatchExpiry}
              />
            </Field>
          </div>
        )}

        <Field label="Observação" htmlFor="entry-reason">
          <input
            id="entry-reason"
            className="input"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Doação, devolução, sobra de outro serviço…"
          />
        </Field>
      </FormSection>
    </Modal>
  );
}

/* =========================== Saída para consumo ========================== */

export function IssueDialog({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const items = useItems();
  const locations = useStockLocations();
  const costCenters = useCostCenters();

  const [itemId, setItemId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [costCenterId, setCostCenterId] = useState('');
  const [quantityMilli, setQuantityMilli] = useState<number | null>(null);
  const [allowExpired, setAllowExpired] = useState(false);
  const [reason, setReason] = useState('');

  useResetOnOpen(open, () => {
    setItemId('');
    setLocationId('');
    setCostCenterId('');
    setQuantityMilli(null);
    setAllowExpired(false);
    setReason('');
  });

  const exigeLote = (items.data ?? []).find((item) => item.id === itemId)?.tracksBatch ?? false;

  const mutation = useMutation({
    mutationFn: (body: unknown) => api.post('/stock-movements/issue', body),
    onSuccess: () => {
      onDone();
      onClose();
    },
  });

  const errors = fieldErrors(mutation.error);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Saída para consumo"
      description="O centro de custo é obrigatório — é ele que transforma saída em custo"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            loading={mutation.isPending}
            disabled={!itemId || !locationId || !costCenterId || !quantityMilli}
            onClick={() =>
              mutation.mutate({
                itemId,
                locationId,
                costCenterId,
                quantityMilli,
                allowExpired,
                reason: reason.trim() || null,
              })
            }
          >
            Registrar saída
          </Button>
        </>
      }
    >
      <ErrorBanner error={mutation.error} />
      <FormSection>
        <Field label="Item" htmlFor="issue-item" error={errors.itemId}>
          <select
            id="issue-item"
            className="input"
            value={itemId}
            onChange={(event) => setItemId(event.target.value)}
          >
            <option value="">Selecione…</option>
            {(items.data ?? []).map((item) => (
              <option key={item.id} value={item.id}>
                {item.sku} · {item.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Local de origem" htmlFor="issue-location" error={errors.locationId}>
          <select
            id="issue-location"
            className="input"
            value={locationId}
            onChange={(event) => setLocationId(event.target.value)}
          >
            <option value="">Selecione…</option>
            {(locations.data ?? []).map((location) => (
              <option key={location.id} value={location.id}>
                {location.code} · {location.name}
              </option>
            ))}
          </select>
        </Field>

        <BalanceHint itemId={itemId} locationId={locationId} />

        <Field label="Centro de custo" htmlFor="issue-cc" error={errors.costCenterId}>
          <select
            id="issue-cc"
            className="input"
            value={costCenterId}
            onChange={(event) => setCostCenterId(event.target.value)}
          >
            <option value="">Selecione…</option>
            {(costCenters.data ?? []).map((center) => (
              <option key={center.id} value={center.id}>
                {center.code} · {center.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Quantidade" htmlFor="issue-qty" error={errors.quantityMilli}>
          <QtyInput id="issue-qty" value={quantityMilli} onChange={setQuantityMilli} />
        </Field>

        {exigeLote && (
          <>
            <p className="muted" style={{ fontSize: 12.5, marginTop: -4 }}>
              O sistema tira primeiro o lote que vence antes, e pula o que já venceu.
            </p>
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13 }}>
              <input
                type="checkbox"
                checked={allowExpired}
                onChange={(event) => setAllowExpired(event.target.checked)}
                style={{ marginTop: 3 }}
              />
              <span>
                Dar baixa em lote VENCIDO
                <span className="muted" style={{ display: 'block', fontSize: 12 }}>
                  Marque só para descarte. Material vencido não sai para consumo.
                </span>
              </span>
            </label>
          </>
        )}

        <Field label="Quem retirou / para quê" htmlFor="issue-reason">
          <input
            id="issue-reason"
            className="input"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Equipe de alvenaria — muro do estacionamento"
          />
        </Field>
      </FormSection>
    </Modal>
  );
}

/* ============================== Transferência ============================ */

export function TransferDialog({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const items = useItems();
  const locations = useStockLocations();

  const [itemId, setItemId] = useState('');
  const [fromLocationId, setFromLocationId] = useState('');
  const [toLocationId, setToLocationId] = useState('');
  const [quantityMilli, setQuantityMilli] = useState<number | null>(null);
  const [reason, setReason] = useState('');

  useResetOnOpen(open, () => {
    setItemId('');
    setFromLocationId('');
    setToLocationId('');
    setQuantityMilli(null);
    setReason('');
  });

  const mutation = useMutation({
    mutationFn: (body: unknown) => api.post('/stock-movements/transfer', body),
    onSuccess: () => {
      onDone();
      onClose();
    },
  });

  const errors = fieldErrors(mutation.error);
  const sameLocation = Boolean(fromLocationId) && fromLocationId === toLocationId;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Transferir material"
      description="Gera o par saída/entrada — nunca uma ponta sem a outra"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            loading={mutation.isPending}
            disabled={!itemId || !fromLocationId || !toLocationId || sameLocation || !quantityMilli}
            onClick={() =>
              mutation.mutate({
                itemId,
                fromLocationId,
                toLocationId,
                quantityMilli,
                reason: reason.trim() || null,
              })
            }
          >
            Transferir
          </Button>
        </>
      }
    >
      <ErrorBanner error={mutation.error} />
      <FormSection>
        <Field label="Item" htmlFor="transfer-item" error={errors.itemId}>
          <select
            id="transfer-item"
            className="input"
            value={itemId}
            onChange={(event) => setItemId(event.target.value)}
          >
            <option value="">Selecione…</option>
            {(items.data ?? []).map((item) => (
              <option key={item.id} value={item.id}>
                {item.sku} · {item.name}
              </option>
            ))}
          </select>
        </Field>

        <div className="grid-2">
          <Field label="De" htmlFor="transfer-from" error={errors.fromLocationId}>
            <select
              id="transfer-from"
              className="input"
              value={fromLocationId}
              onChange={(event) => setFromLocationId(event.target.value)}
            >
              <option value="">Selecione…</option>
              {(locations.data ?? []).map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Para"
            htmlFor="transfer-to"
            error={errors.toLocationId ?? (sameLocation ? 'Escolha um local diferente da origem' : undefined)}
          >
            <select
              id="transfer-to"
              className="input"
              value={toLocationId}
              onChange={(event) => setToLocationId(event.target.value)}
            >
              <option value="">Selecione…</option>
              {(locations.data ?? []).map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <BalanceHint itemId={itemId} locationId={fromLocationId} />

        <Field label="Quantidade" htmlFor="transfer-qty" error={errors.quantityMilli}>
          <QtyInput id="transfer-qty" value={quantityMilli} onChange={setQuantityMilli} />
        </Field>

        <Field label="Motivo" htmlFor="transfer-reason">
          <input
            id="transfer-reason"
            className="input"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Abastecimento da frente de serviço"
          />
        </Field>
      </FormSection>
    </Modal>
  );
}

/* ================================= Ajuste ================================ */

export function AdjustDialog({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const items = useItems();
  const locations = useStockLocations();

  const [itemId, setItemId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [countedQtyMilli, setCountedQtyMilli] = useState<number | null>(null);
  const [batchCode, setBatchCode] = useState('');
  const [batchExpiry, setBatchExpiry] = useState('');
  const [reason, setReason] = useState('');

  useResetOnOpen(open, () => {
    setItemId('');
    setLocationId('');
    setCountedQtyMilli(null);
    setBatchCode('');
    setBatchExpiry('');
    setReason('');
  });

  const balance = useBalance(itemId, locationId);
  const current = balance.data?.quantityMilli ?? 0;
  const difference = (countedQtyMilli ?? 0) - current;

  // Só a SOBRA precisa de lote: material que apareceu tem de dizer de qual
  // lote é. A falta sai pelo que vence primeiro, sem perguntar nada.
  const exigeLote =
    ((items.data ?? []).find((item) => item.id === itemId)?.tracksBatch ?? false) && difference > 0;

  const mutation = useMutation({
    mutationFn: (body: unknown) => api.post('/stock-movements/adjust', body),
    onSuccess: () => {
      onDone();
      onClose();
    },
  });

  const errors = fieldErrors(mutation.error);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Ajustar saldo"
      description="Informe a quantidade CONTADA — o sistema calcula a diferença"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            loading={mutation.isPending}
            disabled={
              !itemId ||
              !locationId ||
              countedQtyMilli === null ||
              reason.trim().length < 5 ||
              (exigeLote && !batchCode.trim())
            }
            onClick={() =>
              mutation.mutate({
                itemId,
                locationId,
                countedQtyMilli,
                batch: exigeLote ? { code: batchCode.trim(), expiryDate: batchExpiry || null } : null,
                reason: reason.trim(),
              })
            }
          >
            Ajustar
          </Button>
        </>
      }
    >
      <ErrorBanner error={mutation.error} />
      <FormSection>
        <Field label="Item" htmlFor="adjust-item" error={errors.itemId}>
          <select
            id="adjust-item"
            className="input"
            value={itemId}
            onChange={(event) => setItemId(event.target.value)}
          >
            <option value="">Selecione…</option>
            {(items.data ?? []).map((item) => (
              <option key={item.id} value={item.id}>
                {item.sku} · {item.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Local" htmlFor="adjust-location" error={errors.locationId}>
          <select
            id="adjust-location"
            className="input"
            value={locationId}
            onChange={(event) => setLocationId(event.target.value)}
          >
            <option value="">Selecione…</option>
            {(locations.data ?? []).map((location) => (
              <option key={location.id} value={location.id}>
                {location.code} · {location.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Quantidade contada" htmlFor="adjust-qty" error={errors.countedQtyMilli}>
          <QtyInput id="adjust-qty" value={countedQtyMilli} onChange={setCountedQtyMilli} />
        </Field>

        {itemId && locationId && countedQtyMilli !== null && (
          <div
            className="card"
            style={{ padding: 12, marginBottom: 12, borderColor: difference === 0 ? undefined : T.amberLine }}
          >
            <div className="mono" style={{ fontSize: 13 }}>
              Saldo no sistema: <strong>{formatQty(current)}</strong>
            </div>
            <div className="mono" style={{ fontSize: 13, marginTop: 4 }}>
              Diferença a lançar:{' '}
              <strong style={{ color: difference === 0 ? T.inkSoft : difference > 0 ? T.green : T.red }}>
                {difference > 0 ? '+' : ''}
                {formatQty(difference)}
              </strong>
            </div>
          </div>
        )}

        {exigeLote && (
          <div className="grid-2">
            <Field label="Lote da sobra" htmlFor="adjust-batch" error={errors.batch}>
              <input
                id="adjust-batch"
                className="input input--mono"
                value={batchCode}
                onChange={(event) => setBatchCode(event.target.value)}
                placeholder="L2026A"
              />
            </Field>
            <Field label="Validade" htmlFor="adjust-expiry">
              <DateInput
                id="adjust-expiry"
                className="input input--mono"
                value={batchExpiry}
                onChange={setBatchExpiry}
              />
            </Field>
          </div>
        )}

        <Field
          label="Justificativa"
          htmlFor="adjust-reason"
          error={errors.reason}
          hint="Fica na trilha de auditoria com seu nome — escreva o que aconteceu"
        >
          <textarea
            id="adjust-reason"
            className="input"
            rows={3}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Inventário de 19/08: 3 sacos danificados por chuva."
          />
        </Field>

        <p style={{ display: 'flex', gap: 8, fontSize: 12.5, color: T.amber }}>
          <AlertTriangle size={15} aria-hidden style={{ flexShrink: 0, marginTop: 1 }} />
          O ajuste muda o saldo sem documento por trás. O custo médio do item
          não é alterado — só a quantidade.
        </p>
      </FormSection>
    </Modal>
  );
}
